import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { ConversationSummary } from '../../domain/types';

function sectionLabelStyle(): React.CSSProperties {
  return {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--z-dim)',
    padding: '4px 10px',
  };
}

function rowButtonStyle(active: boolean, showSidebarLabels: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: showSidebarLabels ? '6px 8px 6px 12px' : '8px 0',
    justifyContent: showSidebarLabels ? 'flex-start' : 'center',
    borderRadius: 8,
    background: active ? 'var(--z-fill-2)' : 'transparent',
    color: active ? 'var(--z-text-1)' : 'var(--z-text-2)',
    fontSize: 12.5,
    fontWeight: active ? 600 : 500,
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  };
}

function ConversationRow({
  conversation,
  active,
  showSidebarLabels,
  onSelect,
  onDelete,
}: {
  conversation: ConversationSummary;
  active: boolean;
  showSidebarLabels: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [hovering, setHovering] = useState(false);
  const canDelete = hovering && showSidebarLabels;
  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
    >
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        style={{ ...rowButtonStyle(active, showSidebarLabels), flex: '1 1 auto', minWidth: 0 }}
        title={conversation.title}
      >
        {showSidebarLabels ? (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {conversation.title}
          </span>
        ) : (
          <span>💬</span>
        )}
      </button>
      {canDelete ? (
        <button
          type="button"
          onClick={() => onDelete(conversation.id)}
          aria-label={`Eliminar ${conversation.title}`}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--z-dim)',
            cursor: 'pointer',
            fontSize: 13,
            padding: '4px 6px',
            borderRadius: 6,
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

export interface SidebarConversationsSectionProps {
  showSidebarLabels: boolean;
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
}

export const SidebarConversationsSection: React.FC<SidebarConversationsSectionProps> = ({
  showSidebarLabels,
  conversations,
  activeConversationId,
  onNewChat,
  onSelectConversation,
  onDeleteConversation,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {showSidebarLabels ? <div style={sectionLabelStyle()}>Conversaciones</div> : null}

    <button
      type="button"
      onClick={onNewChat}
      style={rowButtonStyle(false, showSidebarLabels)}
      title="Nueva conversación"
    >
      <span
        aria-hidden="true"
        style={{
          width: 24,
          height: 24,
          flex: '0 0 24px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 7,
          background: 'var(--z-accent-soft)',
          color: 'var(--z-accent)',
        }}
      >
        <Sparkles size={15} strokeWidth={2.2} />
      </span>
      {showSidebarLabels ? <span>Nuevo chat</span> : null}
    </button>

    {showSidebarLabels ? (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: 280,
          overflowY: 'auto',
        }}
      >
        {conversations.map((conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            active={conversation.id === activeConversationId}
            showSidebarLabels={showSidebarLabels}
            onSelect={onSelectConversation}
            onDelete={onDeleteConversation}
          />
        ))}
      </div>
    ) : null}
  </div>
);
